/** @jsxImportSource @emotion/react */
import css, { SerializedStyles } from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Select, Spin } from 'antd'
import React, { useEffect, useMemo, useState, useRef } from 'react'
import {
  useProvinces,
  type Province,
} from '@sportninja/common/hooks/useProvinces'
import { paragraphMedium, weightRegular } from 'src/components/css'
import { ErrorLabel } from '../ErrorLabel'
import Text from '../Text'
import { TextInput } from '../TextInput'

/**
 * ProvinceSelectInput component for selecting provinces/states
 * Automatically switches between Select (when provinces exist) and TextInput (when no provinces)
 * @component
 */

type ProvinceSelectInputProps = {
  /** Ref to provinces to be used to store fetched provinces */
  provincesRef?: React.MutableRefObject<Province[]>
  /** Field name for error handling and identification */
  name: string
  /** Current selected province value (id or text) */
  value: string | undefined
  /** Function to update the selected province value */
  onChange: (value: string | undefined) => void
  /** Selected country ID to fetch provinces (accepts both number and string) */
  countryId: number | string | undefined
  /** Object containing field validation errors */
  fieldErrors?: any
  /** Custom error messages */
  customError?: any
  /** Label text displayed above the select/input */
  label?: string
  /** Placeholder text */
  placeholder?: string
  /** Whether to allow clearing the selection */
  allowClear?: boolean
  /** Whether the field is disabled */
  disabled?: boolean
  /** Whether to show search functionality in select mode */
  showSearch?: boolean
  /** Tab index for keyboard navigation */
  tabIndex?: number
  /** Custom CSS styles using emotion css */
  style?: SerializedStyles
  /** Optional callback to receive the fetched provinces array */
  onProvincesLoaded?: (provinces: Province[]) => void
  /** Whether the field is required (shows asterisk after label) */
  required?: boolean
}

export type { Province }

const filterOption = (input: string, option: any) =>
  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

export const ProvinceSelectInput: React.FC<ProvinceSelectInputProps> = ({
  name,
  value,
  onChange,
  countryId,
  fieldErrors = {},
  customError = {},
  label = 'Province/State',
  placeholder = 'Select Province/State',
  allowClear = false,
  disabled = false,
  showSearch = true,
  tabIndex,
  style,
  onProvincesLoaded,
  required = false,
  provincesRef,
}) => {
  // Shared, cached provinces fetch (React Query). Same cache as mobile + the
  // edit-player/roster-player flows — reopening the modal won't refetch.
  const { provinces, loading } = useProvinces(countryId)

  // Track dropdown open state and search value for Tab-to-select
  const [isOpen, setIsOpen] = useState(false)
  const searchRef = useRef('')

  // Notify parent (callback + ref) whenever the resolved province list changes.
  useEffect(() => {
    onProvincesLoaded?.(provinces)
    if (provincesRef) {
      provincesRef.current = provinces
    }
  }, [provinces, onProvincesLoaded, provincesRef])

  // Clear value when switching countries (only in select mode)
  useEffect(() => {
    // Skip validation in text input mode (when provinces.length === 0)
    if (value && provinces.length > 0) {
      // Check if current value is valid for the new province list
      const isValidProvince = provinces.some(
        (province) =>
          province.id.toString() === value || province.name === value
      )
      if (!isValidProvince) {
        onChange(undefined)
      }
    }
    // Note: When provinces.length === 0, we're in text input mode - keep the value
  }, [provinces, value, onChange])

  // Build options array with memoization
  const provinceOptions = useMemo(() => {
    return provinces.map((province) => ({
      label: province.name,
      value: province.id.toString(),
    }))
  }, [provinces])

  // Determine if we should show text input (no provinces) or select (has provinces)
  const hasProvinces = provinces.length > 0

  // Tab-to-select handler
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && isOpen && searchRef.current) {
      const matching = provinceOptions.find((opt) =>
        filterOption(searchRef.current, opt)
      )
      if (matching) {
        e.preventDefault()
        handleChange(matching.value)
        setIsOpen(false)
      }
    }
  }

  // Handle change to validate that only valid province IDs/names are accepted
  const handleChange = (newValue: string | undefined) => {
    // If clearing the selection
    if (newValue === undefined || newValue === null) {
      onChange(undefined)
      return
    }

    // For select mode, validate the value
    if (hasProvinces) {
      const isValidProvince = provinces.some(
        (province) => province.id.toString() === newValue
      )

      if (isValidProvince) {
        onChange(newValue)
      }
      // Invalid values are silently ignored
    } else {
      // For text input mode, accept any value
      onChange(newValue)
    }
  }

  // If no country is selected, show disabled text input
  if (!countryId) {
    return (
      <TextInput
        name={name}
        value={value || ''}
        setValue={onChange}
        fieldErrors={fieldErrors}
        customError={customError}
        label={label}
        placeholder='Select a Country First'
        disabled={true}
        tabIndex={tabIndex}
        style={css`
          ${paragraphMedium}
          ${weightRegular}
            font-size: 16px !important;
          color: ${colors.WHITE};
          width: 100%;
          background: ${colors.SECONDARY_200} !important;
          border-radius: 8px !important;
          height: 40px !important;
          opacity: 0.5 !important;
          ${style}
        `}
        required={required}
      />
    )
  }

  // If loading, show select with loading state
  if (loading) {
    return (
      <>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.WHITE}
          style={`
            align-self: stretch;
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            word-break: break-word;
          `}
        >
          {label}
          {required && (
            <span
              css={css`
                margin-left: 4px;
              `}
            >
              *
            </span>
          )}
        </Text>
        <Select
          placeholder='Loading...'
          disabled={true}
          loading={true}
          value={undefined}
          tabIndex={tabIndex}
          css={css`
            ${paragraphMedium}
            ${weightRegular}
            font-size: 16px !important;
            color: ${colors.WHITE};
            width: 100%;
            background: ${colors.SECONDARY_200} !important;
            border-radius: 8px !important;
            height: 40px !important;
            ${style}
          `}
          notFoundContent={
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 8px;
              `}
            >
              <Spin size='small' />
            </div>
          }
        />
        <ErrorLabel
          fieldErrors={fieldErrors || {}}
          customError={customError || {}}
          name={name}
        />
      </>
    )
  }

  // If no provinces, show text input for free text entry
  if (!hasProvinces) {
    return (
      <TextInput
        name={name}
        value={value || ''}
        setValue={onChange}
        fieldErrors={fieldErrors}
        customError={customError}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        tabIndex={tabIndex}
        style={style}
        required={required}
      />
    )
  }

  // If has provinces, show select dropdown
  return (
    <>
      <style>
        {`
          .ant-select-dropdown {
            z-index: 999999 !important;
          }
          .ant-select-selector {
            background: ${colors.SECONDARY_200} !important;
            border-radius: 8px !important;
          }
          .ant-select-selection-item {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
          .ant-select-dropdown .ant-select-item-option-content {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
        `}
      </style>
      <Text
        variant='paragraphSmall'
        weight='regular'
        color={colors.WHITE}
        style={`
          align-self: stretch;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          word-break: break-word;
        `}
      >
        {label}
        {required && (
          <span
            css={css`
              margin-left: 4px;
            `}
          >
            *
          </span>
        )}
      </Text>
      <Select
        placeholder={placeholder}
        allowClear={allowClear}
        disabled={disabled}
        showSearch={showSearch}
        onChange={handleChange}
        options={provinceOptions}
        value={value || undefined}
        tabIndex={tabIndex}
        filterOption={filterOption}
        optionFilterProp='label'
        mode={undefined}
        notFoundContent='No provinces found'
        open={isOpen}
        onDropdownVisibleChange={setIsOpen}
        onSearch={(val) => {
          searchRef.current = val
        }}
        onKeyDown={handleKeyDown}
        css={css`
          ${paragraphMedium}
          ${weightRegular}
          font-size: 16px !important;
          color: ${colors.WHITE};
          width: 100%;
          background: ${colors.SECONDARY_200} !important;
          border-radius: 8px !important;
          height: 40px !important;
          ${disabled && 'opacity: 0.5 !important'}
          ${style}
        `}
        status={
          fieldErrors[name] || fieldErrors?.includes?.(name) ? 'error' : ''
        }
      />
      <ErrorLabel
        fieldErrors={fieldErrors || {}}
        customError={customError || {}}
        name={name}
      />
    </>
  )
}

export default ProvinceSelectInput
